import { useState, useEffect, useMemo } from 'react'
import api from '../../services/api'
import Loading from '../../components/common/Loading'
import ErrorMessage from '../../components/common/ErrorMessage'
import Button from '../../components/common/Button'
import Modal from '../../components/common/Modal'
import GalleryGrid from './components/GalleryGrid'
import GalleryForm from './components/GalleryForm'
import CategoryManager from './components/CategoryManager'
import YoutubeVideoForm, { YoutubeVideo } from './components/YoutubeVideoForm'
import YoutubeVideoTable from './components/YoutubeVideoTable'
import './Gallery.css'

type ViewMode = 'media' | 'youtube'

interface GalleryCategory {
  id: number
  name: string
  slug: string
  position: number
  media_count: number
}

interface GalleryItem {
  id: number
  file_name: string
  file_path: string
  mime_type: string
  file_size: number
  title: string
  description: string | null
  category_id: number | null
  category_name: string | null
  show_in_gallery: boolean
  url: string
  is_video: boolean
}

function Gallery() {
  const [items, setItems] = useState<GalleryItem[]>([])
  const [categories, setCategories] = useState<GalleryCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeCategory, setActiveCategory] = useState<string>('all')
  const [isFormModalOpen, setIsFormModalOpen] = useState(false)
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<GalleryItem | null>(null)

  // YouTube state
  const [viewMode, setViewMode] = useState<ViewMode>('media')
  const [youtubeVideos, setYoutubeVideos] = useState<YoutubeVideo[]>([])
  const [isYoutubeModalOpen, setIsYoutubeModalOpen] = useState(false)
  const [editingYoutubeVideo, setEditingYoutubeVideo] = useState<YoutubeVideo | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError('')
      const [galleryData, categoryData, youtubeData] = await Promise.all([
        api.get<GalleryItem[]>('/gallery'),
        api.get<GalleryCategory[]>('/gallery/categories'),
        api.get<YoutubeVideo[]>('/youtube-videos'),
      ])
      setItems(galleryData)
      setCategories(categoryData)
      setYoutubeVideos(youtubeData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement')
    } finally {
      setLoading(false)
    }
  }

  const filteredItems = useMemo(() => {
    if (activeCategory === 'all') return items
    const category = categories.find((c) => c.slug === activeCategory)
    if (!category) return items
    return items.filter((item) => item.category_id === category.id)
  }, [items, categories, activeCategory])

  const getCategoryCounts = (): Record<string, number> => {
    const counts: Record<string, number> = { all: items.length }
    categories.forEach((cat) => {
      counts[cat.slug] = items.filter((item) => item.category_id === cat.id).length
    })
    return counts
  }

  const handleEdit = (item: GalleryItem) => {
    setEditingItem(item)
    setIsFormModalOpen(true)
  }

  const handleRemoveFromGallery = async (item: GalleryItem) => {
    if (!confirm('Retirer cet élément de la galerie ?')) return

    try {
      await api.put(`/media/${item.id}/gallery`, { show_in_gallery: false })
      setItems(items.filter((i) => i.id !== item.id))
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur lors de la suppression')
    }
  }

  const handleFormSubmit = async (data: {
    title: string
    description: string
    category_id: number | null
  }) => {
    if (!editingItem) return

    try {
      await api.put(`/media/${editingItem.id}/gallery`, data)
      await fetchData()
      setIsFormModalOpen(false)
      setEditingItem(null)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde')
    }
  }

  const handleUploadComplete = async () => {
    await fetchData()
    setIsUploadModalOpen(false)
  }

  const handleCategoriesChanged = async () => {
    await fetchData()
  }

  // YouTube handlers
  const handleYoutubeEdit = (video: YoutubeVideo) => {
    setEditingYoutubeVideo(video)
    setIsYoutubeModalOpen(true)
  }

  const handleYoutubeDelete = async (video: YoutubeVideo) => {
    try {
      await api.delete(`/youtube-videos/${video.id}`)
      setYoutubeVideos(youtubeVideos.filter((v) => v.id !== video.id))
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur lors de la suppression')
    }
  }

  const handleYoutubeToggleStatus = async (video: YoutubeVideo) => {
    try {
      const newStatus = video.status === 'published' ? 'draft' : 'published'
      await api.put(`/youtube-videos/${video.id}/status`, { status: newStatus })
      setYoutubeVideos(
        youtubeVideos.map((v) =>
          v.id === video.id ? { ...v, status: newStatus } : v
        )
      )
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur lors du changement de statut')
    }
  }

  const handleYoutubeSubmit = (video: YoutubeVideo) => {
    if (editingYoutubeVideo) {
      // Update existing
      setYoutubeVideos(
        youtubeVideos.map((v) => (v.id === video.id ? video : v))
      )
    } else {
      // Add new
      setYoutubeVideos([...youtubeVideos, video])
    }
    setIsYoutubeModalOpen(false)
    setEditingYoutubeVideo(null)
  }

  const filteredYoutubeVideos = useMemo(() => {
    if (activeCategory === 'all') return youtubeVideos
    const category = categories.find((c) => c.slug === activeCategory)
    if (!category) return youtubeVideos
    return youtubeVideos.filter((v) => v.category_id === category.id)
  }, [youtubeVideos, categories, activeCategory])

  const getYoutubeCounts = (): Record<string, number> => {
    const counts: Record<string, number> = { all: youtubeVideos.length }
    categories.forEach((cat) => {
      counts[cat.slug] = youtubeVideos.filter((v) => v.category_id === cat.id).length
    })
    return counts
  }

  if (loading) return <Loading />
  if (error) return <ErrorMessage message={error} />

  return (
    <div className="gallery-module">
      <div className="module-header">
        <h1>Gestion de la Galerie</h1>
        <div className="header-actions">
          <Button variant="outline" onClick={() => setIsCategoryModalOpen(true)}>
            Gerer les categories
          </Button>
          {viewMode === 'media' ? (
            <Button onClick={() => setIsUploadModalOpen(true)}>
              Ajouter un media
            </Button>
          ) : (
            <Button onClick={() => {
              setEditingYoutubeVideo(null)
              setIsYoutubeModalOpen(true)
            }}>
              Ajouter une video YouTube
            </Button>
          )}
        </div>
      </div>

      {/* View Mode Toggle */}
      <div className="view-mode-tabs">
        <button
          className={`view-mode-tab ${viewMode === 'media' ? 'active' : ''}`}
          onClick={() => setViewMode('media')}
        >
          Medias ({items.length})
        </button>
        <button
          className={`view-mode-tab ${viewMode === 'youtube' ? 'active' : ''}`}
          onClick={() => setViewMode('youtube')}
        >
          YouTube ({youtubeVideos.length})
        </button>
      </div>

      <div className="category-tabs">
        <button
          className={`category-tab ${activeCategory === 'all' ? 'active' : ''}`}
          onClick={() => setActiveCategory('all')}
        >
          Tous ({viewMode === 'media' ? getCategoryCounts().all : getYoutubeCounts().all})
        </button>
        {categories.map((category) => (
          <button
            key={category.id}
            className={`category-tab ${activeCategory === category.slug ? 'active' : ''}`}
            onClick={() => setActiveCategory(category.slug)}
          >
            {category.name} ({viewMode === 'media'
              ? getCategoryCounts()[category.slug] || 0
              : getYoutubeCounts()[category.slug] || 0})
          </button>
        ))}
      </div>

      <div className="gallery-content">
        {viewMode === 'media' ? (
          // Media Grid View
          filteredItems.length === 0 ? (
            <div className="empty-state">
              <p>Aucun media dans cette categorie</p>
              <Button onClick={() => setIsUploadModalOpen(true)}>
                Ajouter le premier media
              </Button>
            </div>
          ) : (
            <GalleryGrid
              items={filteredItems}
              onEdit={handleEdit}
              onRemove={handleRemoveFromGallery}
            />
          )
        ) : (
          // YouTube Table View
          filteredYoutubeVideos.length === 0 ? (
            <div className="empty-state">
              <p>Aucune video YouTube dans cette categorie</p>
              <Button onClick={() => {
                setEditingYoutubeVideo(null)
                setIsYoutubeModalOpen(true)
              }}>
                Ajouter la premiere video
              </Button>
            </div>
          ) : (
            <YoutubeVideoTable
              videos={filteredYoutubeVideos}
              onEdit={handleYoutubeEdit}
              onDelete={handleYoutubeDelete}
              onToggleStatus={handleYoutubeToggleStatus}
            />
          )
        )}
      </div>

      <Modal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false)
          setEditingItem(null)
        }}
        title="Modifier le média"
      >
        {editingItem && (
          <GalleryForm
            item={editingItem}
            categories={categories}
            onSubmit={handleFormSubmit}
            onCancel={() => {
              setIsFormModalOpen(false)
              setEditingItem(null)
            }}
          />
        )}
      </Modal>

      <Modal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        title="Ajouter un média à la galerie"
      >
        <GalleryForm
          categories={categories}
          onSubmit={handleUploadComplete}
          onCancel={() => setIsUploadModalOpen(false)}
          isNew={true}
        />
      </Modal>

      <Modal
        isOpen={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
        title="Gerer les categories"
      >
        <CategoryManager
          categories={categories}
          onCategoriesChanged={handleCategoriesChanged}
        />
      </Modal>

      {/* YouTube Video Modal */}
      <Modal
        isOpen={isYoutubeModalOpen}
        onClose={() => {
          setIsYoutubeModalOpen(false)
          setEditingYoutubeVideo(null)
        }}
        title={editingYoutubeVideo ? 'Modifier la video YouTube' : 'Ajouter une video YouTube'}
      >
        <YoutubeVideoForm
          video={editingYoutubeVideo || undefined}
          categories={categories}
          onSubmit={handleYoutubeSubmit}
          onCancel={() => {
            setIsYoutubeModalOpen(false)
            setEditingYoutubeVideo(null)
          }}
        />
      </Modal>
    </div>
  )
}

export default Gallery
